import { Tier, TIER_WEIGHTS } from './chunk-MY2WTNZQ.js';
import { GLOBAL_TENANT } from './chunk-BJ3WKLB6.js';
import { canonicalRung, RUNG_ORDER } from './chunk-INX63VAT.js';
import { asString } from './chunk-UW5NWLXH.js';
import { toStoredChainId } from './chunk-E2IBHV7W.js';

// src/catalog/primary.ts
var PRIMARY_KINDS = ["tag", "label", "group", "entity"];
function applyPrimaryFlags(hits, primaryByKind) {
  const flagged = hits.map((h) => ({
    ...h,
    isPrimary: primaryByKind[h.kind] === h.catalogId
  }));
  return flagged.map((h, i) => [h, i]).sort(([a, ai], [b, bi]) => {
    const pa = a.isPrimary ? 0 : 1;
    const pb = b.isPrimary ? 0 : 1;
    return pa !== pb ? pa - pb : ai - bi;
  }).map(([h]) => h);
}
var PRIMARY_CHOICE_KINDS = [...PRIMARY_KINDS, "address"];
var EMPTY_PRIMARY_STATE = { primaries: {}, hidden: [], choices: [] };
function isChoiceKind(value) {
  return typeof value === "string" && PRIMARY_CHOICE_KINDS.includes(value);
}
function chainKey(chainId) {
  if (chainId == null || chainId === "") return null;
  return toStoredChainId(chainId) ?? chainId;
}
function samePrimaryChain(a, b) {
  return chainKey(a) === chainKey(b);
}
function numberOrNull(value) {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "" && Number.isFinite(Number(value))) {
    return Number(value);
  }
  return null;
}
function stringOrNull(value) {
  return typeof value === "string" && value !== "" ? value : null;
}
function primaryChoiceFromWire(raw, kind) {
  if (!raw || typeof raw !== "object") return null;
  const row = raw;
  const k = row.kind ?? row.catalog_kind ?? kind;
  if (!isChoiceKind(k)) return null;
  const rung = canonicalRung(typeof row.rung === "string" ? row.rung : null);
  if (!rung) return null;
  return {
    kind: k,
    // `hidden: true` is "no primary at this rung" whatever the id field says —
    // the services send both, and the flag is the one that decides.
    catalogId: row.hidden === true ? null : stringOrNull(row.catalog_id),
    rung,
    chainId: stringOrNull(row.chain_id),
    version: numberOrNull(row.version),
    setBy: stringOrNull(row.set_by),
    setAt: typeof row.set_at === "number" || typeof row.set_at === "string" && row.set_at !== "" ? row.set_at : null
  };
}
function primaryStateFromWire(body) {
  if (!body || typeof body !== "object") return EMPTY_PRIMARY_STATE;
  const raw = body;
  const primaries = {};
  if (raw.primaries && typeof raw.primaries === "object") {
    for (const [kind, row] of Object.entries(raw.primaries)) {
      const choice = primaryChoiceFromWire(row, kind);
      if (choice && choice.catalogId) primaries[choice.kind] = choice;
    }
  }
  const hidden = Array.isArray(raw.hidden) ? raw.hidden.filter(isChoiceKind).filter((kind) => !primaries[kind]) : [];
  const choices = Array.isArray(raw.choices) ? raw.choices.map((row) => primaryChoiceFromWire(row)).filter((c) => c !== null) : [];
  return { primaries, hidden: [...new Set(hidden)], choices };
}
function rungRank(rung) {
  return RUNG_ORDER.indexOf(rung);
}
function resolvePrimaryChoices(choices, chainId = null) {
  const chain = chainKey(chainId);
  const ranked = choices.filter((c) => c.chainId == null || chainKey(c.chainId) === chain).map((c) => ({ c, rank: rungRank(c.rung) * 2 + (c.chainId == null ? 0 : 1) })).sort((a, b) => b.rank - a.rank);
  const primaries = {};
  const hidden = [];
  const decided = /* @__PURE__ */ new Set();
  for (const { c } of ranked) {
    if (decided.has(c.kind)) continue;
    decided.add(c.kind);
    if (c.catalogId) primaries[c.kind] = c;
    else hidden.push(c.kind);
  }
  return { primaries, hidden };
}
function primaryChoiceAt(choices, kind, rung, chainId = null) {
  return choices.find(
    (c) => c.kind === kind && c.rung === rung && samePrimaryChain(c.chainId, chainId)
  );
}
function primaryHiddenBy(state, kind, chainId = null) {
  if (!state.hidden.includes(kind)) return void 0;
  const chain = chainKey(chainId);
  return state.choices.filter(
    (c) => c.kind === kind && c.catalogId === null && (c.chainId == null || chainKey(c.chainId) === chain)
  ).sort((a, b) => rungRank(b.rung) - rungRank(a.rung))[0];
}
var PRIMARY_RUNG_OWNER = {
  user: "you",
  tenant: "your tenant",
  org: "your organization",
  customer: "your customer account",
  global: "the platform"
};
function primarySetBy(rung) {
  if (rung === "user") return "Your choice";
  if (rung === "global") return "Platform default";
  return `Set by ${PRIMARY_RUNG_OWNER[rung]}`;
}
function primaryAppliesTo(rung) {
  if (rung === "user") return "just for you";
  if (rung === "global") return "for everyone";
  return `for ${PRIMARY_RUNG_OWNER[rung]}`;
}

// src/catalog/ownership.ts
var PLATFORM_TIERS = /* @__PURE__ */ new Set([Tier.DEFAULT, Tier.ENTITY_INHERITED, Tier.PROVIDER_CANDIDATE]);
var SCOPE_LABEL = {
  user: "Your override",
  tenant: "Tenant",
  org: "Org",
  customer: "Customer",
  global: "Platform default"
};
var SOLO_SCOPE_LABEL = {
  user: "Your override",
  tenant: "This workspace",
  org: "This workspace",
  customer: "This workspace",
  global: "Platform default"
};
function parseTier(v) {
  return typeof v === "string" && v in TIER_WEIGHTS ? v : void 0;
}
function firstBool(...values) {
  for (const v of values) if (typeof v === "boolean") return v;
  return void 0;
}
function isRealTenant(v) {
  const s = asString(v);
  return s != null && s !== GLOBAL_TENANT && s !== "*";
}
function isPlatformScope(v) {
  return canonicalRung(asString(v)) === "global";
}
function deriveCatalogOwnership(row) {
  const tier = parseTier(row.tier);
  let scope;
  const wireRung = canonicalRung(asString(row.rung)?.toLowerCase());
  const declaredScope = asString(row.scope);
  if (wireRung != null && wireRung !== "user") {
    scope = wireRung;
  } else if (declaredScope === "user" || declaredScope === "tenant" || declaredScope === "org") {
    scope = declaredScope;
  } else if (isRealTenant(row.user_id)) scope = "user";
  else if (isRealTenant(row.tenant_id) || isRealTenant(row.customer_id)) scope = "tenant";
  else if (isRealTenant(row.org_id)) scope = "org";
  else scope = "global";
  const explicitDefault = firstBool(row.is_system_default, row.system_default, row.systemDefault);
  const isSystemDefault = wireRung != null ? wireRung === "global" : explicitDefault != null ? explicitDefault || isPlatformScope(row.scope) : isPlatformScope(row.scope) || scope === "global" && (tier == null || PLATFORM_TIERS.has(tier));
  if (!isSystemDefault && scope === "global") scope = "tenant";
  const explicitLock = row.editable === false || row.read_only === true || row.readonly === true;
  const readOnly = explicitLock || isSystemDefault;
  return {
    scope,
    tier,
    isSystemDefault,
    readOnly,
    label: SCOPE_LABEL[scope],
    soloLabel: SOLO_SCOPE_LABEL[scope],
    customized: presentationOverrideOf(row) != null,
    shared: scope === "org" || scope === "customer"
  };
}
function layerOf(raw) {
  if (raw == null || typeof raw !== "object" || Array.isArray(raw)) return null;
  const layer = raw;
  const rung = canonicalRung(asString(layer.rung));
  if (rung == null) return null;
  return {
    rung,
    fields: layer.fields && typeof layer.fields === "object" ? layer.fields : {},
    updated_at: typeof layer.updated_at === "number" ? layer.updated_at : null,
    updated_by: typeof layer.updated_by === "string" ? layer.updated_by : null
  };
}
function presentationOverrideOf(row) {
  const raw = row.presentation_override;
  if (raw == null || typeof raw !== "object" || Array.isArray(raw)) return null;
  const marker = raw;
  return {
    fields: marker.fields && typeof marker.fields === "object" ? marker.fields : {},
    overridden: Array.isArray(marker.overridden) ? marker.overridden.filter((f) => typeof f === "string") : [],
    base: marker.base && typeof marker.base === "object" ? marker.base : {},
    layers: Array.isArray(marker.layers) ? marker.layers.map(layerOf).filter((l) => l != null) : [],
    updated_at: typeof marker.updated_at === "number" ? marker.updated_at : null,
    updated_by: typeof marker.updated_by === "string" ? marker.updated_by : null
  };
}
var REACH = {
  customer: "your customer",
  org: "your org",
  tenant: "this workspace"
};
function customisationReach(row) {
  const marker = presentationOverrideOf(row);
  if (!marker) return null;
  const words = marker.layers.map((layer) => REACH[layer.rung]).filter((w) => Boolean(w));
  if (words.length === 0) return "this workspace";
  return words.length === 1 ? words[0] : `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}`;
}
function isReadOnlyCatalogRow(row) {
  return deriveCatalogOwnership(row).readOnly;
}

export { EMPTY_PRIMARY_STATE, PRIMARY_KINDS, PRIMARY_RUNG_OWNER, applyPrimaryFlags, customisationReach, deriveCatalogOwnership, isReadOnlyCatalogRow, presentationOverrideOf, primaryAppliesTo, primaryChoiceAt, primaryChoiceFromWire, primaryHiddenBy, primarySetBy, primaryStateFromWire, resolvePrimaryChoices, samePrimaryChain };
//# sourceMappingURL=chunk-5EECHZ3B.js.map
//# sourceMappingURL=chunk-5EECHZ3B.js.map